import React, { useEffect, useState } from 'react';
import { DollarSign, RefreshCw, Plus, TrendingUp } from 'lucide-react';
import { Modal, Button, FormField, Input, Select, PageHeader, Badge } from '../../components/UI';
import Breadcrumb from '../../components/Breadcrumb';
import { partnerService } from '../../api/services';
import { formatCurrency } from '../../utils/helpers';
import { toJalali } from '../../utils/jalali';
import toast from 'react-hot-toast';

const PARTNERS = {
  partner_halim:     'Abdul Halim Adib Karimi',
  partner_nuroollah: 'Nuroollah Mansoor',
};
const TYPE_COLOR = { contribution:'green', expense:'orange', settlement:'blue', repayment:'red' };

function fmtAmount(tx) {
  if (tx.currency === 'USD') {
    return `$${Number(tx.amount).toLocaleString()} (AFN ${Number(tx.amountInAFN||0).toLocaleString()})`;
  }
  return `AFN ${Number(tx.amount).toLocaleString()}`;
}

export default function PartnersPage() {
  const [summary,     setSummary]   = useState(null);
  const [loading,     setLoading]   = useState(false);
  const [activeTab,   setActiveTab] = useState('partner_halim');

  // Add transaction modal
  const [txOpen,   setTxOpen]   = useState(false);
  const [txForm,   setTxForm]   = useState({ partner:'partner_halim', type:'contribution', amount:'', currency:'AFN', exchangeRate:'90', description:'', date:'' });
  const [txSaving, setTxSaving] = useState(false);

  // Repay modal
  const [repayOpen,   setRepayOpen]   = useState(false);
  const [repayForm,   setRepayForm]   = useState({ partner:'', amount:'', currency:'AFN', exchangeRate:'90', description:'' });
  const [repaySaving, setRepaySaving] = useState(false);

  const fetchSummary = async () => {
    setLoading(true);
    try { const r = await partnerService.getSummary(); setSummary(r.data.data); }
    catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchSummary(); }, []);

  const handleAddTx = async () => {
    if (!txForm.amount || Number(txForm.amount) <= 0) return toast.error('Amount required');
    setTxSaving(true);
    try {
      await partnerService.addTransaction(txForm);
      toast.success('Transaction recorded');
      setTxOpen(false); fetchSummary();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setTxSaving(false); }
  };

  const handleRepay = async () => {
    if (!repayForm.partner || !repayForm.amount || Number(repayForm.amount) <= 0)
      return toast.error('Partner and amount required');
    setRepaySaving(true);
    try {
      await partnerService.repay(repayForm);
      toast.success('Repayment recorded');
      setRepayOpen(false); fetchSummary();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
    finally { setRepaySaving(false); }
  };

  const partnerData = summary?.[activeTab];

  return (
    <div>
      <Breadcrumb/>
      <PageHeader title="Partner Management"
        subtitle="Track partner contributions, expenses and settlements"
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchSummary} disabled={loading}>
              <RefreshCw size={15} className={loading?'animate-spin':''}/>
            </Button>
            <Button variant="outline" onClick={()=>{ setRepayForm({partner:activeTab,amount:'',currency:'AFN',exchangeRate:'90',description:''}); setRepayOpen(true); }}>
              <DollarSign size={15}/> Repay Partner
            </Button>
            <Button onClick={()=>{ setTxForm(p=>({...p,partner:activeTab})); setTxOpen(true); }}>
              <Plus size={15}/> Add Transaction
            </Button>
          </div>
        }
      />

      {/* Partner Balance Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {Object.entries(PARTNERS).map(([key, name]) => {
          const p = summary?.[key];
          return (
            <div key={key} onClick={()=>setActiveTab(key)}
              className={`rounded-xl border-2 p-5 cursor-pointer transition-all ${
                activeTab===key ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-blue-200'
              }`}>
              <div className="flex justify-between items-start mb-3">
                <div>
                  <p className="text-xs text-gray-400 mb-1">Partner</p>
                  <p className="text-base font-bold text-gray-800">{name}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-bold ${(p?.balance||0)>0?'text-orange-600':'text-green-600'}`}>
                    AFN {(p?.balance||0).toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">Balance Owed</p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-green-50 rounded-lg p-2 text-center">
                  <p className="text-gray-400">Contributed</p>
                  <p className="font-bold text-green-700 mt-0.5">{formatCurrency(p?.totalContributed||0)}</p>
                  {p?.transactions?.filter(t=>['contribution'].includes(t.type)&&t.currency==='USD').length>0 && (
                    <p className="text-xs text-blue-600">${p.transactions.filter(t=>t.type==='contribution'&&t.currency==='USD').reduce((s,t)=>s+t.amount,0).toLocaleString()} USD</p>
                  )}
                </div>
                <div className="bg-blue-50 rounded-lg p-2 text-center">
                  <p className="text-gray-400">Repaid</p>
                  <p className="font-bold text-blue-700 mt-0.5">{formatCurrency(p?.totalRepaid||0)}</p>
                </div>
                <div className="bg-orange-50 rounded-lg p-2 text-center">
                  <p className="text-gray-400">Net Balance</p>
                  <p className={`font-bold mt-0.5 ${(p?.balance||0)>0?'text-orange-600':'text-gray-500'}`}>{formatCurrency(p?.balance||0)}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Transactions Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center">
          <p className="text-sm font-semibold text-gray-700">{PARTNERS[activeTab]} — Transactions</p>
          <span className="text-xs text-gray-400">{partnerData?.transactions?.length||0} records</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500">Description</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500">Amount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading ? (
                <tr><td colSpan={4} className="text-center py-10 text-gray-400">Loading...</td></tr>
              ) : !partnerData?.transactions?.length ? (
                <tr><td colSpan={4} className="text-center py-10 text-gray-400">No transactions yet</td></tr>
              ) : partnerData.transactions.map(tx=>(
                <tr key={tx._id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-400">{toJalali(tx.date)}</td>
                  <td className="px-4 py-3"><Badge color={TYPE_COLOR[tx.type]||'gray'}>{tx.type}</Badge></td>
                  <td className="px-4 py-3 text-gray-600 text-sm">{tx.description||'-'}</td>
                  <td className={`px-4 py-3 text-right font-semibold text-sm ${tx.type==='repayment'?'text-red-500':'text-green-700'}`}>
                    {tx.type==='repayment'?'- ':'+ '}{fmtAmount(tx)}
                  </td>
                </tr>
              ))}
            </tbody>
            {(partnerData?.transactions?.length||0)>0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan={3} className="px-4 py-3 text-sm font-semibold text-gray-600">Balance Owed to Partner</td>
                  <td className={`px-4 py-3 text-right text-base font-bold ${(partnerData?.balance||0)>0?'text-orange-600':'text-green-700'}`}>
                    {formatCurrency(partnerData?.balance||0)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {/* Add Transaction Modal */}
      <Modal isOpen={txOpen} onClose={()=>setTxOpen(false)} title="Add Partner Transaction" size="sm">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Partner" required>
              <Select value={txForm.partner} onChange={e=>setTxForm(p=>({...p,partner:e.target.value}))}>
                {Object.entries(PARTNERS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
              </Select>
            </FormField>
            <FormField label="Type">
              <Select value={txForm.type} onChange={e=>setTxForm(p=>({...p,type:e.target.value}))}>
                <option value="contribution">Contribution</option>
                <option value="expense">Expense Paid</option>
                <option value="settlement">Settlement</option>
              </Select>
            </FormField>
            <FormField label="Amount" required>
              <Input type="number" step="0.01" min="0" value={txForm.amount}
                onChange={e=>setTxForm(p=>({...p,amount:e.target.value}))} placeholder="0"/>
            </FormField>
            <FormField label="Currency">
              <Select value={txForm.currency} onChange={e=>setTxForm(p=>({...p,currency:e.target.value}))}>
                <option value="AFN">AFN (افغانی)</option>
                <option value="USD">USD (دالر)</option>
              </Select>
            </FormField>
            {txForm.currency==='USD' && (
              <div className="col-span-2">
                <FormField label="Exchange Rate (1 USD = ? AFN)">
                  <Input type="number" value={txForm.exchangeRate}
                    onChange={e=>setTxForm(p=>({...p,exchangeRate:e.target.value}))} placeholder="90"/>
                </FormField>
                {txForm.amount && (
                  <p className="text-xs text-blue-600 mt-1">
                    = AFN {(Number(txForm.amount) * Number(txForm.exchangeRate||90)).toLocaleString()}
                  </p>
                )}
              </div>
            )}
            <FormField label="Date">
              <Input type="date" value={txForm.date} onChange={e=>setTxForm(p=>({...p,date:e.target.value}))}/>
            </FormField>
            <div className="col-span-2">
              <FormField label="Description">
                <Input value={txForm.description} onChange={e=>setTxForm(p=>({...p,description:e.target.value}))} placeholder="Notes"/>
              </FormField>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={()=>setTxOpen(false)}>Cancel</Button>
            <Button onClick={handleAddTx} loading={txSaving}><Plus size={14}/> Record</Button>
          </div>
        </div>
      </Modal>

      {/* Repay Modal */}
      <Modal isOpen={repayOpen} onClose={()=>setRepayOpen(false)} title="Repay Partner from Pharmacy Cash" size="sm">
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-3 text-xs text-blue-700">
            Deducts from Pharmacy Cash and records in accounting.
          </div>
          <FormField label="Partner" required>
            <Select value={repayForm.partner} onChange={e=>setRepayForm(p=>({...p,partner:e.target.value}))}>
              <option value="">Select partner</option>
              {Object.entries(PARTNERS).map(([k,v])=><option key={k} value={k}>{v}</option>)}
            </Select>
          </FormField>
          {repayForm.partner && summary?.[repayForm.partner] && (
            <div className="bg-orange-50 rounded-lg p-3 text-sm">
              Balance owed: <strong className="text-orange-600">{formatCurrency(summary[repayForm.partner].balance)}</strong>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <FormField label="Amount" required>
              <Input type="number" step="0.01" min="0" value={repayForm.amount}
                onChange={e=>setRepayForm(p=>({...p,amount:e.target.value}))} placeholder="0"/>
            </FormField>
            <FormField label="Currency">
              <Select value={repayForm.currency} onChange={e=>setRepayForm(p=>({...p,currency:e.target.value}))}>
                <option value="AFN">AFN</option>
                <option value="USD">USD</option>
              </Select>
            </FormField>
          </div>
          {repayForm.currency==='USD' && (
            <FormField label="Exchange Rate (1 USD = ? AFN)">
              <Input type="number" value={repayForm.exchangeRate}
                onChange={e=>setRepayForm(p=>({...p,exchangeRate:e.target.value}))} placeholder="90"/>
            </FormField>
          )}
          <FormField label="Description">
            <Input value={repayForm.description} onChange={e=>setRepayForm(p=>({...p,description:e.target.value}))} placeholder="Reason"/>
          </FormField>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={()=>setRepayOpen(false)}>Cancel</Button>
            <Button onClick={handleRepay} loading={repaySaving} className="bg-green-600 hover:bg-green-700 text-white">
              <DollarSign size={14}/> Record Repayment
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
